/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { lazy } from 'react';
import { createBrowserRouter } from 'react-router';

import { AppShell } from '@/app/AppShell';
import { ProtectedRoute } from '@/auth/ProtectedRoute';
import { RequireCapability } from './RequireCapability';
import { RouteErrorBoundary } from './RouteErrorBoundary';
import UnauthorizedPage from './pages/UnauthorizedPage';
import { load } from './lazyRoutes';
import { withReload } from './staleChunk';

// Re-exported so existing imports (main.tsx, tests) keep resolving from '@/routes/router' after the
// stale-chunk recovery helpers moved to ./staleChunk (breaks the router <-> boundary import cycle).
export {
  isChunkLoadError,
  reloadForStaleChunk,
  withReload,
  installStaleChunkReload,
} from './staleChunk';

const HomePage = lazy(withReload(load['/']));
const ExplorerPage = lazy(withReload(load['/schematic-explorer']));
const MeclPage = lazy(withReload(load['/mecl']));
const SchematicsPage = lazy(withReload(load['/schematics']));
const Viewer3DPage = lazy(withReload(load['/viewer-3d']));
const ResourceLinksPage = lazy(withReload(load['/resource-links']));
const AnalyticsPage = lazy(withReload(load['/analytics']));
const AdminPage = lazy(withReload(load['/admin']));
const NotFoundPage = lazy(withReload(load['*']));

export const router = createBrowserRouter([
  // Public route for expired/forbidden sessions outside the protected shell. Not lazy-loaded: a
  // dead session lands here, and the edge that rejected it rejects the chunk request too.
  { path: '/unauthorized', element: <UnauthorizedPage />, errorElement: <RouteErrorBoundary /> },
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppShell />
      </ProtectedRoute>
    ),
    // Catch stale-chunk / CSS-preload errors after a redeploy and self-heal instead of showing
    // React Router's raw default error screen.
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <HomePage /> },
      { path: 'schematic-explorer', element: <ExplorerPage /> },
      { path: 'mecl', element: <MeclPage /> },
      { path: 'schematics', element: <SchematicsPage /> },
      { path: 'viewer-3d', element: <Viewer3DPage /> },
      { path: 'resource-links', element: <ResourceLinksPage /> },
      {
        path: 'analytics',
        element: (
          <RequireCapability capability="analytics.view">
            <AnalyticsPage />
          </RequireCapability>
        ),
      },
      {
        path: 'admin/*',
        element: (
          <RequireCapability capability="admin.console">
            <AdminPage />
          </RequireCapability>
        ),
      },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
]);
